import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Container, Box } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';

import Header from './components/Header';
import Home from './pages/Home';
import BookingPage from './pages/BookingPage';
import AdminPage from './pages/AdminPage';
import Academy from './pages/Academy';
import CancelBookingPage from './pages/CancelBookingPage';
import AboutUsPage from './pages/AboutUsPage';
import ContactUsPage from './pages/ContactUsPage';

const theme = createTheme({
  palette: {
    primary: {
      main: '#2E7D32', // Professional green for sports
    },
    secondary: {
      main: '#1565C0', // Professional blue
    },
    background: {
      default: 'transparent',
      paper: 'rgba(255, 255, 255, 0.95)', // Semi-transparent white for cards
    },
    text: {
      primary: '#1a1a1a',
      secondary: '#555555',
    }
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontWeight: 600,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    }
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          backdropFilter: 'blur(10px)',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '12px',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backdropFilter: 'blur(10px)',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          textTransform: 'none',
          fontWeight: 600,
          padding: '10px 24px',
        },
        contained: {
          boxShadow: '0 4px 12px rgba(46, 125, 50, 0.3)',
          '&:hover': {
            boxShadow: '0 6px 20px rgba(46, 125, 50, 0.4)',
          },
        },
      },
    },
  },
});

// Needs to be a child of <Router> to read the current route via useLocation - the admin
// dashboard is a dense data table that wants the full window width, while the public
// marketing pages keep their existing centered "lg" layout.
function AppContent() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  return (
        <Box
          className="App"
          sx={{
            minHeight: '100vh',
            background: `
              linear-gradient(135deg, rgba(46, 125, 50, 0.1) 0%, rgba(21, 101, 192, 0.1) 100%),
              linear-gradient(45deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.2) 100%),
              url('/turf.jpeg')
            `,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed',
            backgroundRepeat: 'no-repeat',
            position: 'relative',
          }}
        >
          {/* Overlay for better readability */}
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
              backdropFilter: 'blur(1px)',
              zIndex: -1,
            }}
          />
          
          <Header />
          
          <Container
            maxWidth={isAdminRoute ? false : "lg"}
            sx={{
              mt: 4,
              mb: 4,
              position: 'relative',
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                minHeight: 'calc(100vh - 200px)',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/booking" element={<BookingPage />} />
                  <Route path="/admin" element={<AdminPage />} />
                  <Route path="/academy" element={<Academy />} />
                  <Route path="/cancel-booking/:bookingId" element={<CancelBookingPage />} />
                  <Route path="/about" element={<AboutUsPage />} />
                  <Route path="/contact" element={<ContactUsPage />} />
                </Routes>
            </Box>
          </Container>
          
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            toastStyle={{
              backdropFilter: 'blur(10px)',
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              color: '#1a1a1a',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            }}
          />
        </Box>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AppContent />
      </Router>
    </ThemeProvider>
  );
}

export default App;